import React, { useState, useEffect } from 'react';
import { 
    Modal, Form, Input, Button, Spin, 
    Select, Alert, Space, Popconfirm,
    Typography, Tag, Tooltip, Divider
} from 'antd';
import { 
    SaveOutlined, InfoCircleOutlined, 
    CloudOutlined, NotificationOutlined,
    AmazonOutlined, DeleteOutlined,
    CheckCircleOutlined, SyncOutlined
} from '@ant-design/icons';

const { Text, Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const SqsDestinationModal = ({ 
    visible, 
    onCancel, 
    onSave, 
    editingAccount,
    loading
}) => {
    const [form] = Form.useForm();
    const [isExistingDestination, setIsExistingDestination] = useState(false);
    const [isCheckingStatus, setIsCheckingStatus] = useState(false);
    
    const defaultEventTypes = ['ANY_OFFER_CHANGED', 'ORDER_CHANGE'];
    const allEventTypes = [
        { value: 'ANY_OFFER_CHANGED', label: 'Any Offer Changed' },
        { value: 'ORDER_CHANGE', label: 'Order Change' },
        { value: 'ITEM_QUANTITY_CHANGE', label: 'Item Quantity Change' },
        { value: 'PRODUCT_TYPE_ASSIGNMENT_CHANGE', label: 'Product Type Assignment Change' },
        { value: 'ITEM_PRODUCT_TYPE_CHANGE', label: 'Item Product Type Change' },
        { value: 'LISTINGS_ITEM_STATUS_CHANGE', label: 'Listings Item Status Change' },
        { value: 'LISTINGS_ITEM_ISSUES_CHANGE', label: 'Listings Item Issues Change' },
        { value: 'BRANDED_ITEM_CONTENT_CHANGE', label: 'Branded Item Content Change' },
        { value: 'FEE_PROMOTION', label: 'Fee Promotion' }
    ];
    
    // Reset form và kiểm tra SQS destination khi modal mở hoặc khi dữ liệu account thay đổi
    useEffect(() => {
        if (visible) {
            console.log('Modal opened with account:', editingAccount);
            form.resetFields();
            
            // Luôn đặt event types mặc định ban đầu
            form.setFieldsValue({
                eventTypes: defaultEventTypes
            });
            
            // Kiểm tra dữ liệu destination nếu đã có
            if (editingAccount) {
                checkForExistingDestination(editingAccount);
            }
        }
    }, [visible, editingAccount, form]);
    
    // Hàm kiểm tra SQS destination từ dữ liệu editingAccount
    const checkForExistingDestination = (account) => {
        // Kiểm tra xem trong account đã có thông tin về SQS destination chưa
        if (account && account.notificationDestination && account.notificationDestination.destinationId) {
            console.log('Found existing destination in account data:', account.notificationDestination);
            
            // Đánh dấu là đã có destination và lưu dữ liệu
            setIsExistingDestination(true);
            
            // Ưu tiên eventTypes array, fallback đến subscriptions array, cuối cùng là default
            const currentEventTypes = account.notificationDestination.eventTypes?.length > 0 
                ? account.notificationDestination.eventTypes
                : account.notificationDestination.subscriptions?.length > 0
                    ? account.notificationDestination.subscriptions.map(s => s.eventType).filter(Boolean)
                    : defaultEventTypes;
            
            console.log('Current event types for form:', currentEventTypes);
            
            form.setFieldsValue({
                name: account.notificationDestination.destinationName || '',
                queueUrl: account.notificationDestination.sqsQueueUrl || '',
                eventTypes: currentEventTypes
            });
        } else {
            console.log('No existing destination found in account data');
            setIsExistingDestination(false);
            
            // Đảm bảo form có giá trị mặc định khi không có dữ liệu
            form.setFieldsValue({
                name: '',
                queueUrl: '',
                eventTypes: defaultEventTypes
            });
        }
    };
    
    const handleSubmit = () => {
        form.validateFields().then(values => {
            // Nếu đang cập nhật destination đã tồn tại thì gửi kèm destinationId
            if (isExistingDestination && editingAccount?.notificationDestination?.destinationId) {
                onSave({
                    sellerId: editingAccount?.id,
                    destinationId: editingAccount.notificationDestination.destinationId,
                    updateType: 'updateSubscriptions',
                    eventTypes: values.eventTypes
                });
            } else {
                // Tạo mới destination
                onSave({
                    sellerId: editingAccount?.id,
                    name: values.name,
                    queueUrl: values.queueUrl,
                    eventTypes: values.eventTypes
                });
            }
        });
    };
    
    const handleDelete = () => {
        if (editingAccount?.notificationDestination?.destinationId) {
            onSave({
                sellerId: editingAccount.id,
                destinationId: editingAccount.notificationDestination.destinationId,
                updateType: 'deleteDestination'
            });
        }
    };
    
    const handleCheckStatus = () => {
        if (!editingAccount?.id || !editingAccount?.notificationDestination?.destinationId) {
            return;
        }
        
        setIsCheckingStatus(true);
        
        // Gọi API để kiểm tra trạng thái
        onSave({
            sellerId: editingAccount.id,
            destinationId: editingAccount.notificationDestination.destinationId,
            updateType: 'checkStatus'
        }).then(result => {
            if (result.success) {
                // Dữ liệu sẽ được cập nhật tự động bởi SellerAccountSettings
            }
        }).finally(() => {
            setIsCheckingStatus(false);
        });
    };
    
    // Xác định tiêu đề modal và nút hành động dựa trên trạng thái destination
    const modalTitle = isExistingDestination ? 
        <Space>
            <AmazonOutlined style={{ color: '#ff9900' }} />
            <span>Quản lý Amazon SP-API SQS Destination</span>
        </Space> :
        <Space>
            <AmazonOutlined style={{ color: '#ff9900' }} />
            <span>Cấu hình Amazon SP-API SQS Destination</span>
        </Space>;
    
    const modalOkText = isExistingDestination ? 'Cập nhật Subscriptions' : 'Tạo Destination';
    const modalOkIcon = isExistingDestination ? <SyncOutlined /> : <SaveOutlined />;
    
    // Render nội dung loading hoặc form dựa trên trạng thái
    const renderContent = () => {
        return (
            <>
                {loading ? (
                    <Alert
                        message="Đang kiểm tra SQS destination"
                        description="Đang kiểm tra với Amazon SP-API để xác thực thông tin SQS destination cho tài khoản seller này..."
                        type="info"
                        showIcon
                        icon={<SyncOutlined spin />}
                        style={{ marginBottom: 16 }}
                    />
                ) : isExistingDestination ? (
                    <Alert
                        message={
                            <Space>
                                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                <Text strong>SQS Destination đã được cấu hình</Text>
                            </Space>
                        }
                        description={
                            <div>
                                <p>Tài khoản seller này đã có SQS destination được cấu hình để nhận thông báo từ Amazon SP-API. Bạn có thể cập nhật subscriptions hoặc xóa destination.</p>
                                <p>
                                    <Text strong>Destination ID:</Text> {editingAccount?.notificationDestination?.destinationId}
                                </p>
                                <Space style={{ marginTop: 8 }}>
                                    <Button 
                                        size="small" 
                                        icon={<SyncOutlined />} 
                                        onClick={handleCheckStatus}
                                        loading={isCheckingStatus}
                                    >
                                        Kiểm tra trạng thái
                                    </Button>
                                </Space>
                            </div>
                        }
                        type="success"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                ) : (
                    <Alert
                        message="SQS Destination cho Amazon SP-API Notifications"
                        description="Cấu hình Amazon SQS destination để nhận thông báo theo thời gian thực về các thay đổi đơn hàng và cập nhật kho trực tiếp từ Amazon Selling Partner API. Thông tin xác thực AWS từ worker được gán sẽ được sử dụng để ủy quyền cho Amazon gửi thông báo đến SQS queue của bạn."
                        type="info"
                        showIcon
                        icon={<NotificationOutlined />}
                        style={{ marginBottom: 16 }}
                    />
                )}
                
                <Form 
                    form={form} 
                    layout="vertical" 
                    className="sqs-destination-form"
                >
                    <Form.Item
                        name="name"
                        label="Tên Destination"
                        rules={[{ required: true, message: 'Vui lòng nhập tên destination' }]}
                    >
                        <Input 
                            placeholder="Nhập tên cho destination này (vd: Orders-Notifications)" 
                            maxLength={64}
                            disabled={isExistingDestination || loading}
                        />
                    </Form.Item>
                    
                    <Form.Item
                        name="queueUrl"
                        label="SQS Queue URL hoặc ARN"
                        rules={[
                            { required: !isExistingDestination, message: 'Vui lòng nhập SQS queue URL hoặc ARN' },
                            { 
                                pattern: /^(https:\/\/sqs\.[a-z0-9-]+\.amazonaws\.com\/[0-9]{12}\/[a-zA-Z0-9_-]+|arn:aws:sqs:[a-z0-9-]+:[0-9]{12}:[a-zA-Z0-9_-]+)$/,
                                message: 'Vui lòng nhập SQS queue URL hoặc ARN hợp lệ' 
                            }
                        ]}
                        extra="URL hoặc ARN của Amazon SQS queue của bạn (vd: arn:aws:sqs:ap-southeast-1:123456789012:queue-name hoặc https://sqs.region.amazonaws.com/account-id/queue-name)"
                    >
                        <Input 
                            placeholder="arn:aws:sqs:ap-southeast-1:123456789012:queue-name" 
                            disabled={isExistingDestination || loading}
                        />
                    </Form.Item>
                    
                    <Divider orientation="left">Subscriptions</Divider>
                    
                    <Form.Item
                        name="eventTypes"
                        label="Loại sự kiện cần theo dõi"
                        rules={[{ required: true, message: 'Vui lòng chọn ít nhất một loại sự kiện' }]}
                        help="Chọn các loại sự kiện bạn muốn nhận từ Amazon SP-API"
                    >
                        <Select 
                            placeholder="Chọn loại sự kiện" 
                            mode="multiple"
                            showSearch
                            optionFilterProp="children"
                            disabled={loading}
                        >
                            {allEventTypes.map(eventType => (
                                <Option key={eventType.value} value={eventType.value}>{eventType.label}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                    
                    <Alert
                        message="Sử dụng thông tin xác thực của Worker"
                        description="Thông tin xác thực AWS từ worker được gán sẽ được sử dụng để ủy quyền cho SQS destination này. Đảm bảo worker của bạn có quyền thích hợp để gửi tin nhắn đến SQS queue này."
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                </Form>
            </>
        );
    };
    
    return (
        <Modal
            title={modalTitle}
            open={visible}
            confirmLoading={loading}
            onOk={handleSubmit}
            onCancel={onCancel}
            width={700}
            okText={modalOkText}
            cancelText="Hủy"
            okButtonProps={{ 
                icon: modalOkIcon,
                loading: loading,
                disabled: loading
            }}
            footer={[
                isExistingDestination && (
                    <Popconfirm
                        key="delete"
                        title="Xóa SQS Destination"
                        description="Bạn có chắc chắn muốn xóa SQS destination này? Điều này sẽ dừng tất cả thông báo từ Amazon."
                        onConfirm={handleDelete}
                        okText="Có, Xóa"
                        cancelText="Không"
                        placement="topRight"
                        okButtonProps={{ danger: true }}
                    >
                        <Button 
                            danger 
                            icon={<DeleteOutlined />}
                            loading={loading}
                            disabled={loading}
                        >
                            Xóa Destination
                        </Button>
                    </Popconfirm>
                ),
                <Button key="cancel" onClick={onCancel} disabled={loading}>
                    Hủy
                </Button>,
                <Button 
                    key="submit" 
                    type="primary" 
                    onClick={handleSubmit}
                    icon={modalOkIcon}
                    loading={loading}
                    disabled={loading}
                >
                    {modalOkText}
                </Button>
            ].filter(Boolean)}
        >
            <div className="sqs-destination-modal-content">
                {renderContent()}
            </div>
        </Modal>
    );
};

export default SqsDestinationModal;